import React, { useEffect, useState } from "react";
import { CheckCircle2, FileText, Info, Loader2, Save, Upload } from "lucide-react";
import {
  useGetExamApplicationDocumentsQuery,
  useSaveExamApplicationDocumentsMutation,
} from "../api/examCallsApi";
import OlevelSittings, {
  blankSitting,
  storageUrl,
  toEditableSitting,
  validateSittings,
  type Sitting,
} from "./OlevelSittings";
import QualificationRows, {
  blankQualification,
  validateQualifications,
  type QualificationRow,
} from "./QualificationRows";
import ExamCpdPanel from "./ExamCpdPanel";

/**
 * The hand-in: whatever documents this exam asks for, uploaded after payment.
 *
 * WHICH documents is the exam's decision, not this component's. The server
 * answers with the list — O'level, academic qualifications, workshop
 * certificate, technical report, attestation letter, in any combination — and
 * this renders what it is told. It used to be hardcoded to interviews, so a
 * non-interview exam could never ask for anything and every interview had to
 * ask for the same three things.
 *
 * NOTHING IS RE-TYPED THAT THE INSTITUTE ALREADY HOLDS. O'level and academic
 * qualifications pre-fill from the member's record, falling back to their
 * probationer application, and the answer says which it came from so the notice
 * can be honest about how stale it might be.
 *
 * A DEGREE CERTIFICATE IS NOT A SEPARATE UPLOAD. On an exam that is the
 * certificate on an academic qualification, so it is collected inside the
 * qualification record — with the institution, the qualification and the year
 * around it — rather than as a lone file with no context.
 *
 * NOTHING HERE DECIDES whether the application may be submitted. The `missing`
 * list comes from the server, which is what actually refuses at finalize.
 */

/**
 * Where a pre-filled answer came from, said in the candidate's terms.
 *
 * Worth distinguishing: what the Institute holds has been kept current, while
 * something dug out of an old application may be years stale and deserves a
 * closer look before it is signed off again. `application` needs no notice at
 * all — those are the candidate's own answers, already on this form.
 */
function prefillNotice(source?: string | null): string | null {
  if (source === "member") {
    return "Filled in from the record the Institute holds for you. Check it, correct anything that has changed, then save.";
  }
  if (source === "probationer") {
    return "Filled in from your probationer application — we have nothing more recent on file. Check it carefully, update anything that has changed, then save.";
  }
  return null;
}

/** ".pdf,.jpg" from the server's "pdf,jpg" — what the file picker wants. */
const toAccept = (accepts?: string) =>
  (accepts ?? "pdf")
    .split(",")
    .map((e) => `.${e.trim()}`)
    .join(",");

const input =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900";

/** One required document: what is on file, or how to supply it. */
function DocumentField({
  doc, picked, onPick, disabled,
}: {
  doc: any;
  picked: File | null;
  onPick: (f: File | null) => void;
  disabled?: boolean;
}) {
  const stored = doc.stored as string | null;

  return (
    <div className="border border-gray-100 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-900 text-sm">{doc.label}</div>
          <p className="text-xs text-gray-500 mt-0.5">
            {(doc.accepts ?? "pdf").toUpperCase().replace(/,/g, ", ")} · up to 10MB
          </p>
        </div>
        {stored && !picked ? (
          <a
            href={storageUrl(stored) ?? "#"}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline"
          >
            <FileText className="w-3.5 h-3.5" /> On file
          </a>
        ) : null}
      </div>

      {!disabled ? (
        <label className="mt-3 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 font-semibold">
            <Upload className="w-3.5 h-3.5" />
            {stored ? "Replace" : "Choose file"}
          </span>
          <input
            type="file"
            accept={toAccept(doc.accepts)}
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
          {/* Named rather than "1 file selected" — the commonest upload mistake
              is picking the wrong document, and it is only catchable now. */}
          <span className="truncate">
            {picked ? picked.name : stored ? "" : "No file chosen"}
          </span>
        </label>
      ) : null}
    </div>
  );
}

export default function ApplicationDocumentsSection({
  applicationId, showOlevels = true, onSaved,
}: {
  applicationId: string;
  /**
   * False when the full TPC/PCI application form is on screen too — that form
   * owns the same O'level table, and two editors over one table would race.
   */
  showOlevels?: boolean;
  onSaved?: (missing: string[]) => void;
}) {
  const { data, isLoading, isError, refetch } =
    useGetExamApplicationDocumentsQuery(applicationId, { skip: !applicationId });

  const [save, { isLoading: saving }] = useSaveExamApplicationDocumentsMutation();

  const [sittings, setSittings] = useState<Sitting[]>([]);
  const [qualifications, setQualifications] = useState<QualificationRow[]>([]);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [graduationYear, setGraduationYear] = useState("");
  const [cpdConfirmed, setCpdConfirmed] = useState(false);
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const rules = data?.requirements ?? {};
  const cpd = data?.cpd ?? {};
  const canEdit = data?.can_edit !== false;
  const missing: string[] = data?.missing ?? [];
  const documents: any[] = data?.documents ?? [];

  const wantsOlevel = !!data?.requires_olevel && showOlevels;
  const wantsQualifications = !!data?.requires_qualifications;
  const needsGraduationYear = rules.min_years_since_graduation != null;
  const needsCpd = rules.min_cpd_points != null;

  useEffect(() => {
    if (!data) return;
    const incoming = (data.sittings ?? []).map(toEditableSitting);
    setSittings(incoming.length ? incoming : [blankSitting()]);

    const quals: QualificationRow[] = (data.qualifications ?? []).map((q: any) => ({
      institution: q.institution ?? "",
      qualification: q.qualification ?? "",
      year: String(q.year ?? ""),
      cert_file: q.cert_file ?? null,
      file: null,
    }));
    setQualifications(quals.length ? quals : [blankQualification()]);

    setGraduationYear(data.graduation_year ?? "");
    setCpdConfirmed(!!data.cpd?.confirmed_at);
    setFiles({});
  }, [data]);

  const onSave = async () => {
    setBanner(null);

    // The same rules the probationer form applies, checked here so the
    // candidate is told which sitting and what — not handed a bare refusal.
    if (wantsOlevel) {
      const problem = validateSittings(sittings);
      if (problem) {
        setBanner({ ok: false, text: problem });
        return;
      }
    }

    if (wantsQualifications) {
      const problem = validateQualifications(qualifications);
      if (problem) {
        setBanner({ ok: false, text: problem });
        return;
      }
    }

    const body = new FormData();

    if (wantsOlevel) {
      // Says "this request carries the O'level list", so a candidate who
      // removed a sitting can save that — multipart sends nothing for an empty
      // array, which would otherwise read as "not about O'level".
      body.append("sync_olevels", "1");

      sittings.forEach((s, i) => {
        body.append(`sittings[${i}][exam_type]`, s.exam_type || "waec");
        body.append(`sittings[${i}][exam_year]`, s.exam_year);
        if (s.exam_number) body.append(`sittings[${i}][exam_number]`, s.exam_number);
        if (s.result_file) body.append(`sittings[${i}][result_file]`, s.result_file);
        // One scan for the whole sitting — it is one result slip.
        if (s.file) body.append(`sitting_files[${i}]`, s.file);

        s.subjects
          .filter((x) => x.subject.trim() && x.grade.trim())
          .forEach((x, j) => {
            body.append(`sittings[${i}][subjects][${j}][subject]`, x.subject.trim());
            body.append(`sittings[${i}][subjects][${j}][grade]`, x.grade.trim().toUpperCase());
          });
      });
    }

    if (wantsQualifications) {
      body.append("sync_qualifications", "1");

      qualifications
        .filter((q) => q.institution.trim() && q.qualification.trim())
        .forEach((q, i) => {
          body.append(`qualifications[${i}][institution]`, q.institution.trim());
          body.append(`qualifications[${i}][qualification]`, q.qualification.trim());
          if (q.year) body.append(`qualifications[${i}][year]`, q.year);
          if (q.cert_file) body.append(`qualifications[${i}][cert_file]`, q.cert_file);
          if (q.file) body.append(`qualification_files[${i}]`, q.file);
        });
    }

    documents.forEach((doc) => {
      const picked = files[doc.key];
      if (picked) body.append(doc.key, picked);
    });

    if (needsGraduationYear && graduationYear) body.append("graduation_year", graduationYear);
    if (needsCpd) body.append("cpd_confirmed", cpdConfirmed ? "1" : "0");

    try {
      const res: any = await save({ id: applicationId, body }).unwrap();
      setBanner({ ok: true, text: res?.message ?? "Saved." });
      onSaved?.(res?.missing ?? []);
      await refetch();
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || e?.error || "Could not save your documents.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white border rounded-xl p-5 text-sm text-gray-600 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading what this exam asks for…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white border rounded-xl p-5 text-sm text-rose-700">
        Could not load your application documents.{" "}
        <button onClick={() => refetch()} className="underline font-semibold">Retry</button>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-xl p-4 sm:p-5 space-y-5">
      <div>
        <h2 className="font-semibold text-gray-900">Your documents</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          What this exam asks you to hand in with your application.
        </p>
      </div>

      {/* Outstanding items, as the server sees them — the same list that
          refuses the submission, so the two can never disagree. */}
      {missing.length > 0 ? (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="font-semibold">Still to do before you can submit</p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {missing.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      ) : (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          Everything required is in.
        </div>
      )}

      {banner ? (
        <div
          className={`text-xs rounded-lg p-3 border ${
            banner.ok
              ? "text-emerald-800 bg-emerald-50 border-emerald-100"
              : "text-rose-700 bg-rose-50 border-rose-100"
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      {/* ── O'level ─────────────────────────────────────────────────────── */}
      {wantsOlevel ? (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">O'level results</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            English, Mathematics and Physics are compulsory, with at least 5 subjects per
            sitting. You may combine up to 3 sittings.
          </p>

          {/* Said plainly: this is OUR record of them to be corrected, not a
              blank form to retype — and which record it is. */}
          {prefillNotice(data?.olevel_source) ? (
            <div className="mt-3 flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg p-3">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{prefillNotice(data?.olevel_source)}</span>
            </div>
          ) : null}

          <div className="mt-3">
            <OlevelSittings
              sittings={sittings}
              onChange={setSittings}
              disabled={!canEdit}
              onError={(text) => setBanner({ ok: false, text })}
            />
          </div>
        </div>
      ) : null}

      {/* ── Academic qualifications ─────────────────────────────────────── */}
      {wantsQualifications ? (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Academic qualifications</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Your educational history, with a certificate for each.
          </p>

          {prefillNotice(data?.qualifications_source) ? (
            <div className="mt-3 flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg p-3">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{prefillNotice(data?.qualifications_source)}</span>
            </div>
          ) : null}

          <div className="mt-3">
            <QualificationRows
              rows={qualifications}
              onChange={setQualifications}
              disabled={!canEdit}
              onError={(text) => setBanner({ ok: false, text })}
            />
          </div>
        </div>
      ) : null}

      {/* Saving updates the member's own record as well as this application.
          Said before they press the button, not after. */}
      {(wantsOlevel || wantsQualifications) && canEdit ? (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
          Saving keeps these on your Institute record, so your next application starts from
          them instead of asking again. Applications you have already submitted are not
          changed.
        </p>
      ) : null}

      {/* ── Uploads ─────────────────────────────────────────────────────── */}
      {documents.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {documents.map((doc) => (
            <DocumentField
              key={doc.key}
              doc={doc}
              picked={files[doc.key] ?? null}
              onPick={(f) => setFiles((s) => ({ ...s, [doc.key]: f }))}
              disabled={!canEdit}
            />
          ))}
        </div>
      ) : null}

      {/* ── Year of graduation ──────────────────────────────────────────── */}
      {needsGraduationYear ? (
        <div className="border border-gray-100 rounded-xl p-4">
          <div className="font-medium text-gray-900 text-sm">Year of graduation</div>
          <p className="text-xs text-gray-500 mt-0.5">
            This exam requires at least {rules.min_years_since_graduation} year
            {rules.min_years_since_graduation === 1 ? "" : "s"} since graduation.
          </p>
          <input
            className={`${input} mt-3 max-w-[10rem]`}
            value={graduationYear}
            disabled={!canEdit}
            inputMode="numeric"
            maxLength={4}
            onChange={(e) => setGraduationYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="e.g. 2016"
          />
        </div>
      ) : null}

      {/* ── CPD ─────────────────────────────────────────────────────────── */}
      {needsCpd ? (
        <ExamCpdPanel
          cpd={cpd}
          required={Number(rules.min_cpd_points ?? 0)}
          confirmed={cpdConfirmed}
          onConfirmChange={setCpdConfirmed}
          // A fresh claim counts immediately, so the panel and the Submit
          // button above it have to see it without a page reload.
          onClaimed={() => refetch()}
          disabled={!canEdit}
        />
      ) : null}

      {canEdit ? (
        <div className="flex justify-end">
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save documents"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
